import type { DemandStatus, DemandPriority } from '@/lib/api';

/* ── Kanban Column Definitions ────────────────────────────────────────────── */

export interface DemandColumnDef {
  id: string;
  title: string;
  statuses: DemandStatus[];
  /** Título da coluna no kanban quando difere do statgrid (wireframe:
   *  statgrid "Aguard. aprov." × kcol-h "Aguard. aprovação"). */
  kanbanTitle?: string;
}

// Reforma UX §6.4 (DEM-01) + DEM-2614 — kanban por STATUS com 9 colunas.
// Os 13 status canonical do backend (src/demands/types.ts) agrupam assim:
//   draft/planned/ready        → Prontas
//   running/finishing          → Em execução
//   pending_approval/review    → Aguard. aprov.
//   approved                   → Aprovadas
//   done                       → Finalizadas
//   rejected                   → Reprovadas
//   blocked                    → Bloqueadas  (fila viva)
//   failed                     → Falharam    (histórico com laudo; DEM-2614)
//   cancelled                  → Canceladas
// DEM-2614: failed NÃO compartilha coluna com blocked — a UI mentia mostrando
// failed histórico como "Bloqueada" e inflava a fila de bloqueio real.
export const DEMAND_COLUMNS: DemandColumnDef[] = [
  { id: 'prontas', title: 'Prontas', statuses: ['draft', 'planned', 'ready'] },
  { id: 'em_execucao', title: 'Em execução', statuses: ['running', 'finishing'] },
  { id: 'aguardando_aprovacao', title: 'Aguard. aprov.', kanbanTitle: 'Aguard. aprovação', statuses: ['pending_approval', 'review'] },
  { id: 'aprovadas', title: 'Aprovadas', statuses: ['approved'] },
  { id: 'finalizadas', title: 'Finalizadas', statuses: ['done'] },
  { id: 'reprovadas', title: 'Reprovadas', statuses: ['rejected'] },
  { id: 'bloqueadas', title: 'Bloqueadas', statuses: ['blocked'] },
  { id: 'falharam', title: 'Falharam', statuses: ['failed'] },
  { id: 'canceladas', title: 'Canceladas', statuses: ['cancelled'] },
];

/* ── Status → column id ───────────────────────────────────────────────────── */

// Derivado de DEMAND_COLUMNS pra não duplicar o mapeamento nos cards.
const STATUS_TO_COLUMN: Record<string, string> = Object.fromEntries(
  DEMAND_COLUMNS.flatMap((col) => col.statuses.map((s) => [s, col.id])),
);

/** Coluna do kanban pra um demands.status arbitrário. Fallback: Prontas. */
export function columnIdForStatus(status: string): string {
  return STATUS_TO_COLUMN[status] ?? 'prontas';
}

/* ── Column → first status (for drag-drop transitions) ────────────────────── */

export const COLUMN_DEFAULT_STATUS: Record<string, DemandStatus> = {
  prontas: 'ready',
  em_execucao: 'running',
  aguardando_aprovacao: 'pending_approval',
  aprovadas: 'approved',
  finalizadas: 'done',
  reprovadas: 'rejected',
  bloqueadas: 'blocked',
  falharam: 'failed',
  canceladas: 'cancelled',
};

/* ── Status Labels ────────────────────────────────────────────────────────── */

export const STATUS_LABEL: Record<DemandStatus, string> = {
  draft: 'Rascunho',
  planned: 'Planejada',
  ready: 'Pronta',
  running: 'Executando',
  pending_approval: 'Aprovacao',
  review: 'Revisao',
  approved: 'Aprovada',
  rejected: 'Reprovada',
  finishing: 'Finalizando',
  done: 'Finalizada',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  blocked: 'Bloqueada',
};

/**
 * Label PT-BR para um demands.status arbitrário (string). Usa STATUS_LABEL
 * quando o status é canonical; fallback retorna o próprio status raw —
 * defesa anti-desync (status novo no backend não some, só aparece técnico).
 * Análogo a runStatusLabel (pipeline-counts.ts) mas pra demands.status.
 */
export function demandStatusLabel(status: string): string {
  return STATUS_LABEL[status as DemandStatus] ?? status;
}

/* ── Status Colors ────────────────────────────────────────────────────────── */
//
// Cores espelham o wireframe (kdot.ready=accent, run=info, wait=amber,
// appr=success, done=success-text, rej/block=danger, canc=muted).

export const COLUMN_BORDER: Record<string, string> = {
  prontas: 'border-l-sky-500',
  em_execucao: 'border-l-blue-500',
  aguardando_aprovacao: 'border-l-amber-500',
  aprovadas: 'border-l-teal-500',
  finalizadas: 'border-l-emerald-500',
  reprovadas: 'border-l-rose-500',
  bloqueadas: 'border-l-red-500',
  falharam: 'border-l-red-500',
  canceladas: 'border-l-zinc-500',
};

export const COLUMN_DOT: Record<string, string> = {
  prontas: 'bg-sky-500',
  em_execucao: 'bg-blue-500',
  aguardando_aprovacao: 'bg-amber-500',
  aprovadas: 'bg-teal-500',
  finalizadas: 'bg-emerald-500',
  reprovadas: 'bg-rose-500',
  bloqueadas: 'bg-red-500',
  falharam: 'bg-red-500',
  canceladas: 'bg-zinc-500',
};

/**
 * Tom do wireframe por coluna — usado pelo statgrid (WfKpi tone, wireframe
 * .stat.ready/.run/...) e pelo kdot do kanban (.wf-kdot.ready/.run/... em
 * wf-demands.css). Mesmos sufixos do wireframe canônico.
 */
export const COLUMN_WF_TONE: Record<
  string,
  'ready' | 'run' | 'wait' | 'appr' | 'done' | 'rej' | 'block' | 'canc'
> = {
  prontas: 'ready',
  em_execucao: 'run',
  aguardando_aprovacao: 'wait',
  aprovadas: 'appr',
  finalizadas: 'done',
  reprovadas: 'rej',
  bloqueadas: 'block',
  // DEM-2614: reusa tone 'block' (danger) — failed é histórico com laudo, mesma
  // severidade visual de blocked; WfKpiTone já tem block/rej, sem mudar WfKpi.
  falharam: 'block',
  canceladas: 'canc',
};

/** Cor do número nas métricas consolidadas do topo (wireframe .stat .sv). */
export const COLUMN_STAT_TEXT: Record<string, string> = {
  prontas: 'text-sky-300',
  em_execucao: 'text-blue-300',
  aguardando_aprovacao: 'text-amber-300',
  aprovadas: 'text-teal-300',
  finalizadas: 'text-emerald-300',
  reprovadas: 'text-rose-300',
  bloqueadas: 'text-red-300',
  falharam: 'text-red-300',
  canceladas: 'text-raios-muted',
};

/* ── Priority Badges ──────────────────────────────────────────────────────── */

type BadgeVariant = 'danger' | 'warning' | 'brand' | 'default';

export const PRIORITY_BADGE: Record<DemandPriority, { variant: BadgeVariant; label: string }> = {
  critical: { variant: 'danger', label: 'Critical' },
  high: { variant: 'warning', label: 'High' },
  medium: { variant: 'brand', label: 'Medium' },
  low: { variant: 'default', label: 'Low' },
};

/* ── Filter Items ─────────────────────────────────────────────────────────── */
//
// Reforma UX §6.4 (DEM-03) — filtros: negócio · runtime · supervisão ·
// prioridade. Labels PT-BR conforme wireframe. Runtime sem 'arqueops'
// (backend DemandRuntimeTarget = raios|openclaw|manual). Supervisão mantém
// 'auto' (existe no enum backend, mesmo fora do wireframe) pra não esconder
// demands reais com esse modo.

export const PRIORITY_FILTER_ITEMS = [
  { value: 'all', label: 'Prioridade: todas' },
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
];

export const RUNTIME_FILTER_ITEMS = [
  { value: 'all', label: 'Runtime: todos' },
  { value: 'raios', label: 'RAIOS' },
  { value: 'openclaw', label: 'OpenClaw' },
  { value: 'manual', label: 'Manual' },
];

export const SUPERVISION_FILTER_ITEMS = [
  { value: 'all', label: 'Supervisão: todas' },
  { value: 'supervised', label: 'Supervised' },
  { value: 'unsupervised', label: 'Unsupervised' },
  { value: 'auto', label: 'Auto' },
];

/* ── Nova demanda (modal) ─────────────────────────────────────────────────── */
//
// Opções dos selects do modal "Nova demanda" (wireframe dlgNewDemand).
// Valores = enums canonical do backend (src/demands/types.ts).

export const NEW_DEMAND_PRIORITY_OPTIONS = [
  { value: 'medium', label: 'Média' },
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'low', label: 'Baixa' },
];

export const NEW_DEMAND_RUNTIME_OPTIONS = [
  { value: 'raios', label: 'RAIOS' },
  { value: 'openclaw', label: 'OpenClaw' },
  { value: 'manual', label: 'Manual' },
];

export const NEW_DEMAND_SUPERVISION_OPTIONS = [
  { value: 'supervised', label: 'Supervised' },
  { value: 'unsupervised', label: 'Unsupervised' },
];

/**
 * Status que o Dashboard agrega como "Em execução" / "Em andamento"
 * (mesmo agrupamento do kanban DEMAND_COLUMNS.em_execucao e do BE
 * DEMAND_STATUS_TO_DASHBOARD_KEY: running + finishing → running).
 * DEM-2592: fonte única pra lista "Em andamento" não divergir do card KPI.
 */
export const IN_PROGRESS_STATUSES: readonly DemandStatus[] = ['running', 'finishing'];

/**
 * Status que o Dashboard agrega como "Aguardando aprovação"
 * (mesmo agrupamento do kanban DEMAND_COLUMNS.aguardando_aprovacao e do BE
 * DEMAND_STATUS_TO_DASHBOARD_KEY: pending_approval + review → pending_approval).
 */
export const WAITING_APPROVAL_STATUSES: readonly DemandStatus[] = [
  'pending_approval',
  'review',
];

/**
 * FONTE ÚNICA: demanda "em andamento / em execução" (card KPI + lista do Dash).
 * Inclui `finishing` — status intermediário ainda em execução (split sync/async).
 * Antes a lista do Dash só filtrava `running` e sumia a demanda enquanto o card
 * ainda contava finishing como execução (mismatch card×lista — DEM-2592).
 */
export function demandIsInProgress(
  demand: { status?: string | null } | null | undefined,
): boolean {
  const s = demand?.status ?? '';
  return s === 'running' || s === 'finishing';
}

/**
 * FONTE ÚNICA: uma demanda precisa de DECISÃO HUMANA (mostra o card de aprovação com
 * os botões ✓ aprovar / ✕ reprovar) quando está em `pending_approval` OU `review`.
 *
 * NÃO gateia por `supervision_mode`. Antes, Dashboard e DemandRichCard duplicavam a
 * regra `(pending_approval||review) && supervised` — então uma demanda UNSUPERVISED que
 * escalava pra `pending_approval` (run não auto-completável: PR aberto/não-mergeado, dep
 * faltando) ficava ZUMBI: esperando aprovação mas SEM botão pra aprovar. Se chegou em
 * pending_approval/review, por definição precisa de um humano — o botão TEM que aparecer
 * (supervised OU unsupervised). O backend aceita aprovar unsupervised (approver_type='none').
 */
export function demandAwaitsApproval(
  demand: { status?: string | null } | null | undefined,
): boolean {
  const s = demand?.status ?? '';
  return s === 'pending_approval' || s === 'review';
}
